/**
 * SchedulerPanel — the intelligent, predictive agenda (CSV: "intelligent dynamic
 * scheduler, agenda-style, predictive blocks"). Built from REAL data:
 *   - get_calendar_stress_advisory (business) → load/conflict banner
 *   - next_best_actions (business)            → predicted priority actions
 *   - get_open_obligations (business)         → due/needs-doing, laid into blocks
 *   - get_situation (core)                    → live situational context
 * The "prediction" is a deterministic ranking (due date + status + priority)
 * folded into morning/afternoon/evening focus blocks — never fabricated events.
 * Tap any block or action to pull it into the central chat.
 */
import { useMemo } from "react";
import { CalendarClock, AlertTriangle, Sunrise, Sun, Sunset, ChevronRight } from "lucide-react";
import McpCard from "../components/McpCard";
import { useMcpTool } from "../lib/useMcpTool";
import { PanelScroll } from "./ClientsPanel";
import { setChatContext } from "../lib/chatContext";

interface Obligation { id?: number; folder?: string; text?: string; status?: string; next_step?: string; due_date?: string; priority?: string | number }
interface Action { title?: string; action?: string; text?: string; client?: string; folder?: string; reason?: string; rationale?: string; priority?: string | number; [k: string]: unknown }

const rows = <T,>(d: unknown, ...keys: string[]): T[] => {
  if (Array.isArray(d)) return d as T[];
  if (d && typeof d === "object") for (const k of keys) { const v = (d as Record<string, unknown>)[k]; if (Array.isArray(v)) return v as T[]; }
  return [];
};
const txt = (d: unknown): string => (typeof d === "string" ? d : d && typeof d === "object" ? ((d as Record<string, unknown>).advisory ?? (d as Record<string, unknown>).summary ?? (d as Record<string, unknown>).text ?? "") as string : "");

const dueRank = (o: Obligation): number => {
  const s = (o.status ?? "").toUpperCase();
  let r = s.includes("BLOCK") ? 0 : s.includes("PROGRESS") ? 1 : 2;
  if (o.due_date) { const d = Date.parse(o.due_date); if (!Number.isNaN(d)) { const days = (d - Date.now()) / 86400000; if (days < 0) r -= 2; else if (days < 2) r -= 1; } }
  return r;
};

export default function SchedulerPanel() {
  const stress = useMcpTool<unknown>("business", "get_calendar_stress_advisory", {});
  const nba = useMcpTool<unknown>("business", "next_best_actions", { limit: 12 });
  const obligations = useMcpTool<Obligation[]>("business", "get_open_obligations", { limit: 100 });
  const situation = useMcpTool<unknown>("core", "get_situation", {});

  const obs = useMemo(() => [...rows<Obligation>(obligations.data, "obligations", "rows")].sort((a, b) => dueRank(a) - dueRank(b)), [obligations.data]);
  const actions = rows<Action>(nba.data, "actions", "next_best_actions", "rows", "items");

  // Predictive focus blocks: split ranked obligations into morning (top third,
  // hardest/soonest), afternoon (middle), evening (wrap-up / low priority).
  const blocks = useMemo(() => {
    const n = obs.length;
    const third = Math.ceil(n / 3) || 1;
    return [
      { key: "morning", label: "Morning — deep & due-soon", icon: <Sunrise size={15} />, items: obs.slice(0, third) },
      { key: "afternoon", label: "Afternoon — client & ops", icon: <Sun size={15} />, items: obs.slice(third, third * 2) },
      { key: "evening", label: "Evening — wrap-up", icon: <Sunset size={15} />, items: obs.slice(third * 2) },
    ];
  }, [obs]);

  const stressText = txt(stress.data);
  const situationText = txt(situation.data);
  const loading = stress.loading || nba.loading || obligations.loading;

  const discuss = (label: string, detail: string) => setChatContext({ label, detail });

  return (
    <PanelScroll accentVar="--biome-business" title="Agenda" subtitle="intelligent · predictive · live">
      {(stressText || situationText) && (
        <div className="rounded-xl border bg-surface p-4 space-y-2" style={{ borderLeft: "3px solid var(--biome-business)" }}>
          {stressText && (
            <div className="flex items-start gap-2">
              <AlertTriangle size={15} className="text-warning mt-0.5 shrink-0" />
              <span className="text-body-sm text-text leading-relaxed">{stressText.slice(0, 400)}</span>
            </div>
          )}
          {situationText && <div className="text-caption text-text-muted leading-relaxed pl-7">{situationText.slice(0, 300)}</div>}
        </div>
      )}

      <McpCard title="Predicted focus blocks" loading={loading} error={obligations.error} onRefresh={() => { obligations.refresh(); stress.refresh(); nba.refresh(); }} accentVar="--biome-business">
        {obs.length === 0 ? (
          <div className="text-body-sm text-text-dim">Nothing open to schedule — inbox zero.</div>
        ) : (
          <div className="space-y-4">
            {blocks.filter((b) => b.items.length).map((b) => (
              <div key={b.key}>
                <div className="flex items-center gap-2 text-text-muted mb-1.5"><span className="text-primary">{b.icon}</span><span className="text-h3 text-text font-semibold">{b.label}</span><span className="text-caption text-text-dim">{b.items.length}</span></div>
                <div className="space-y-1">
                  {b.items.map((o, i) => (
                    <button key={o.id ?? i} onClick={() => discuss(`${o.folder ?? "task"}: ${(o.text ?? "").slice(0, 40)}`, `${o.folder ? o.folder + " — " : ""}${o.text}${o.next_step ? `\nNext: ${o.next_step}` : ""}${o.due_date ? `\nDue: ${o.due_date}` : ""}`)}
                      className="w-full flex items-center gap-2 text-left px-2 py-1.5 rounded-md hover:bg-surface-hover group">
                      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: dueRank(o) <= 0 ? "var(--danger)" : dueRank(o) === 1 ? "var(--warning)" : "var(--text-dim)" }} />
                      <span className="flex-1 min-w-0">
                        <span className="block text-body-sm text-text truncate">{o.text}</span>
                        {(o.folder || o.next_step) && <span className="block text-caption text-text-dim truncate">{o.folder}{o.next_step ? ` · → ${o.next_step}` : ""}</span>}
                      </span>
                      {o.due_date && <span className="font-mono text-caption text-text-dim shrink-0">{o.due_date}</span>}
                      <ChevronRight size={13} className="text-text-dim opacity-0 group-hover:opacity-60 shrink-0" />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </McpCard>

      {actions.length > 0 && (
        <McpCard title={`Next best actions (${actions.length})`} loading={nba.loading} error={nba.error} onRefresh={nba.refresh} accentVar="--biome-business">
          <div className="space-y-1">
            {actions.map((a, i) => {
              const t = a.title ?? a.action ?? a.text ?? "(action)";
              const why = a.reason ?? a.rationale ?? "";
              return (
                <button key={i} onClick={() => discuss(`Action: ${String(t).slice(0, 40)}`, `${t}${a.client || a.folder ? `\nClient: ${a.client ?? a.folder}` : ""}${why ? `\nWhy: ${why}` : ""}`)}
                  className="w-full flex items-start gap-2 text-left px-2 py-1.5 rounded-md hover:bg-surface-hover group">
                  <CalendarClock size={14} className="text-primary mt-0.5 shrink-0" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-body-sm text-text">{t}</span>
                    {(a.client || a.folder || why) && <span className="block text-caption text-text-dim">{a.client ?? a.folder ?? ""}{why ? ` · ${why}` : ""}</span>}
                  </span>
                </button>
              );
            })}
          </div>
        </McpCard>
      )}
    </PanelScroll>
  );
}
